import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  User, TrendingUp, Award, Target, CheckCircle, ArrowLeft, Video,
  BarChart2, Search, Plus, Filter, SortAsc, ChevronDown, ChevronUp,
  GridIcon, List, RefreshCw, MessageSquare,
  Trophy, X, Smartphone, Users, Settings
} from 'lucide-react';
import VideoAnalyticsModal from './VideoAnalyticsModal';
import PlayerChatbot from './player_profiles/PlayerChatbot';
import MyPlayerProfileCard from './MyPlayerProfileCard';
import NILPlayerCard from './NILPlayerCard';
import apiClient from '../utils/apiClient';
import { capitalizeFirstWord, getAllSports, getSelectedSport, getSetting, getSportTitle, getUserSettings, isCBB } from '../utils/helpers';
import { useTheme } from '../utils/theme';
import { positionsBaseball, positionsMBB } from '../utils/constant';
import UploadTrainingStats from './player_profiles/UploadTrainingStats';
import { getMBBRoasters } from '../utils/MBBApis';
import { fetchMLBTeamRoster } from '../lib/mlbRosterService';
import { getBasketballPlayers } from '../utils/basketballDataService';

/**
 * Generate team logo path based on team name and sport
 * @param {string} teamName - The team name
 * @param {string} sport - Sport code (MLB, NBA, CBB, etc.)
 * @returns {string} Logo path
 */
const getTeamLogoPath = (teamName, sport) => {
  if (!teamName) return '/logos/SLAMBallLogo.svg';

  // Convert team name to slug (lowercase with hyphens)
  const slug = teamName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .trim();

  // Map sport codes to logo directories
  const sportDirs = {
    MLB: 'MLB',
    NBA: 'NBA',
    WNBA: 'WNBA',
    MBB: 'CBB',
    CBB: 'CBB',
    WBB: 'CBB',
  };

  const dir = sportDirs[sport] || 'NCAA';
  return `/logos/${dir}/${slug}.png`;
};

/**
 * TeamBanner - Prominent horizontal banner showing selected team
 */
const TeamBanner = ({ team, sport, playerCount, isDarkMode }) => {
  const logoPath = getTeamLogoPath(team?.team_name || team?.name, sport);
  const teamName = team?.team_name || team?.name || 'Select a Team';

  // Fallback logo handling
  const [logoError, setLogoError] = useState(false);
  const fallbackLogo = '/logos/SLAMBallLogo.svg';

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-6"
      style={{
        background: 'linear-gradient(135deg, rgba(20,20,25,0.95) 0%, rgba(15,15,20,0.98) 100%)',
        border: '1px solid rgba(232,184,0,0.15)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 60px rgba(232,184,0,0.05)',
      }}
    >
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFD700' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Gradient Overlay */}
      <div
        className="absolute right-0 top-0 w-1/3 h-full pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(232,184,0,0.08) 100%)',
        }}
      />

      <div className="relative flex items-center gap-6 p-6">
        {/* Team Logo */}
        <div
          className="relative flex-shrink-0 w-24 h-24 rounded-2xl flex items-center justify-center overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          }}
        >
          <img
            src={logoError ? fallbackLogo : logoPath}
            alt={teamName}
            className="w-16 h-16 object-contain"
            onError={() => setLogoError(true)}
          />
        </div>

        {/* Team Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h1
              className="text-2xl lg:text-3xl font-bold truncate"
              style={{ color: '#ffffff' }}
            >
              {teamName}
            </h1>
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(232,184,0,0.2) 0%, rgba(255,170,0,0.2) 100%)',
                color: '#E8B800',
                border: '1px solid rgba(232,184,0,0.3)',
              }}
            >
              {sport}
            </span>
          </div>
          <p
            className="text-sm flex items-center gap-2"
            style={{ color: 'rgba(255,255,255,0.5)' }}
          >
            <Users size={14} />
            {playerCount > 0 ? `${playerCount} Active Roster Players` : 'Loading roster...'}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="hidden lg:flex items-center gap-6">
          <div className="text-center">
            <div
              className="text-2xl font-bold font-mono"
              style={{ color: '#E8B800' }}
            >
              {playerCount}
            </div>
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: 'rgba(255,255,255,0.4)' }}
            >
              Players
            </div>
          </div>
          <div
            className="w-px h-10"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          />
          <div className="text-center">
            <div
              className="text-2xl font-bold font-mono"
              style={{ color: '#ffffff' }}
            >
              2026
            </div>
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: 'rgba(255,255,255,0.4)' }}
            >
              Season
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sample player data - updated to include all formats
export const samplePlayers = []
// Helper function to map API response to component format
const mapApiResponseToPlayerFormat = (apiPlayer) => {
  // Determine player type based on position
  let playerType = "hitter";
  if (apiPlayer?.position?.includes("P") ||
    apiPlayer?.position?.includes("pitcher")) {
    playerType = "pitcher";
  }
  if (apiPlayer?.position?.includes("C") ||
    apiPlayer?.position?.toLowerCase() === "catcher") {
    playerType = "catcher";
  }

  // Extract season/year from various possible API fields
  const season = apiPlayer?.season || apiPlayer?.roster_year || apiPlayer?.year || apiPlayer?.academic_year;

  // Extract team from various possible API fields
  const team = apiPlayer?.team_name || apiPlayer?.team || apiPlayer?.school || apiPlayer?.latest_team;

  return {
    id: apiPlayer?.id || apiPlayer?.player_id || apiPlayer?.players,
    PlayerID: apiPlayer?.id || apiPlayer?.player_id || apiPlayer?.players,
    Player: apiPlayer?.name || apiPlayer?.player_name,
    name: apiPlayer?.name || apiPlayer?.player_name,
    Position: apiPlayer?.position,
    position: apiPlayer?.position,
    playerType: playerType,
    Year: apiPlayer?.year,
    year: apiPlayer?.year,
    Height: apiPlayer?.height,
    height: apiPlayer?.height,
    Weight: apiPlayer?.weight,
    weight: apiPlayer?.weight,
    class: apiPlayer?.year || apiPlayer?.class,
    hometown: apiPlayer?.hometown,
    photo: apiPlayer?.image || apiPlayer?.image_url,
    // Team and season fields for filtering
    team: team,
    latest_team: team,
    season: season,
    roster_year: season,
    stats: {
      batting: {
        avg: apiPlayer?.batting_average || 0,
        hr: apiPlayer?.home_runs || 0,
        rbi: apiPlayer?.rbis || 0
      },
      pitching: {
        era: apiPlayer?.era || 0,
        so: apiPlayer?.strikeouts || 0,
        whip: apiPlayer?.whip || 0
      },
      catching: apiPlayer?.position?.includes("C") ? {
        csPercent: apiPlayer?.caught_stealing_percentage || 35
      } : null
    }
  };
};

// Get player color theme based on position/type
export const getPlayerTheme = (player) => {
  const colorMap = {
    default: {
      primary: 'blue',
      accent: 'blue-600',
      light: 'blue-50',
      border: 'blue-100',
      hover: 'blue-500'
    },
    hitter: {
      primary: 'green',
      accent: 'green-600',
      light: 'green-50',
      border: 'green-100',
      hover: 'green-500'
    },
    catcher: {
      primary: 'amber',
      accent: 'amber-600',
      light: 'amber-50',
      border: 'amber-100',
      hover: 'amber-500'
    },
    pitcher: {
      primary: player?.throws === 'Right' ? 'indigo' : 'purple',
      accent: player?.throws === 'Right' ? 'indigo-600' : 'purple-600',
      light: player?.throws === 'Right' ? 'indigo-50' : 'purple-50',
      border: player?.throws === 'Right' ? 'indigo-100' : 'purple-100',
      hover: player?.throws === 'Right' ? 'indigo-500' : 'purple-500'
    }
  };

  return colorMap[player?.playerType] || colorMap.default;
};

// Get expanded position name
export const getPositionName = (positionCode) => {
  const positions = {
    'C': 'Catcher',
    '1B': 'First Base',
    '2B': 'Second Base',
    '3B': 'Third Base',
    'SS': 'Shortstop',
    'IF': 'Infielder',
    'OF': 'Outfielder',
    'LF': 'Left Field',
    'CF': 'Center Field',
    'RF': 'Right Field',
    'DH': 'Designated Hitter',
    'UT': 'Utility',
    'RHP': 'Right-Handed Pitcher',
    'LHP': 'Left-Handed Pitcher',
    'P': 'Pitcher',
    'OF/2B': 'Outfield/Second Base'
  };

  return positions[positionCode] || positionCode;
};

// PlayerCard component for the roster list view
export const PlayerCard = ({ player, onClick, onChatClick }) => {
  // Determine player type based on position
  const playerType = player?.Position?.includes('P') ? 'pitcher' :
    player?.Position?.includes('C') ? 'catcher' : 'hitter';

  const theme = getPlayerTheme({ ...player, playerType });
  const positionName = getPositionName(player?.Position);
  const { theme: appTheme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  return (
    <div className="w-full max-w-md mx-auto">
      <div className={`relative rounded-xl overflow-hidden shadow-lg ${'bg-gray-800'} text-white`}>
        {/* Player Image Background */}
        <div className="h-48 goldenGradient from-blue-900 to-gray-900 relative">
          <div className="absolute inset-0 bg-black opacity-40"></div>
          <div className="absolute bottom-0 left-0 p-4 z-10">
            <div className="text-xs font-semibold uppercase tracking-wider mb-1 themeSecondary inline-block px-2 py-1 rounded-sm">
              {player?.position}
            </div>
            <h2 className="text-2xl font-bold">{player?.name}</h2>
            <p className="text-sm text-gray-300">
              {player?.year} • {player?.height} • {player?.weight}
            </p>
            {/* Show team if available */}
            {player?.team && (
              <p className="text-xs text-yellow-400 mt-1 font-medium">
                {player.team}
              </p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4">
          {/* Stats Section */}
          <div className="grid grid-cols-2 gap-4">
            {playerType === 'pitcher' ? (
              <>
                <div className={`${'bg-gray-700'} p-3 rounded`}>
                  <p className="text-xs text-gray-400 mb-1">ERA</p>
                  <p className="font-semibold">{player?.era?.toFixed(2) || '0.00'}</p>
                </div>
                <div className={`${'bg-gray-700'} p-3 rounded`}>
                  <p className="text-xs text-gray-400 mb-1">STRIKEOUTS</p>
                  <p className="font-semibold">{player?.strikeouts || '0'}</p>
                </div>
              </>
            ) : (
              <>
                <div className={`${'bg-gray-700'} p-3 rounded`}>
                  <p className="text-xs text-gray-400 mb-1">BATTING AVG</p>
                  <p className="font-semibold">{player?.batting_average?.toFixed(3) || '.000'}</p>
                </div>
                <div className="bg-gray-800 p-3 rounded">
                  <p className="text-xs text-gray-400 mb-1">HOME RUNS</p>
                  <p className="font-semibold">{player?.home_runs || '0'}</p>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 mt-6">
            <button className="themeSecondary text-white py-2 rounded-lg font-medium hoverintoGray transition" onClick={onClick}>
              View Full Profile
            </button>
            <button onClick={(e) => {
              e.stopPropagation();
              onChatClick(player?.name);
            }} className="themeSecondary text-black py-2 rounded-lg font-medium  transition">
              Chat with Roster
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Filter component for roster list
const RosterFilter = ({ onFilter, activeFilter, setActiveFilter }) => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const filters = [
    { id: 'all', label: 'All Rosters' },
    { id: 'hitter', label: 'Hitters' },
    { id: 'pitcher', label: 'Pitchers' },
    { id: 'catcher', label: 'Catchers' }
  ];

  return (
    <div className="flex space-x-2 mb-6">
      {filters.map(filter => (
        <button
          key={filter.id}
          onClick={() => {
            setActiveFilter(filter.id);
            onFilter({ type: 'playerType', value: filter.id });
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === filter.id
            ? 'themeSecondary text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
};

// Component for position-specific filtering - Modernized 2026 Glass Design
const PositionFilter = ({ onFilter, activePosition, setActivePosition }) => {
  const sportCode = getSelectedSport();
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  // Basketball sports use basketball positions, baseball sports use baseball positions
  const basketballSports = ['NBA', 'WNBA', 'MBB', 'WBB'];
  const positions = basketballSports.includes(sportCode) ? positionsMBB : positionsBaseball;

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {positions.map((position) => {
          const isActive = activePosition === position.id;
          return (
            <button
              key={position.id}
              onClick={() => {
                setActivePosition(position.id);
                onFilter({ type: "position", value: position.id });
              }}
              className="relative px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                background: isActive
                  ? 'linear-gradient(135deg, #E8B800 0%, #FFA500 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)',
                backdropFilter: 'blur(12px)',
                border: isActive
                  ? '1px solid rgba(232,184,0,0.5)'
                  : '1px solid rgba(255,255,255,0.1)',
                boxShadow: isActive
                  ? '0 4px 20px rgba(232,184,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
                color: isActive
                  ? '#000'
                  : 'rgba(255,255,255,0.8)',
              }}
            >
              {position.label}
            </button>
          );
        })}
    </div>
  );
};

// Pro sports leagues that don't use NIL (Name, Image, Likeness)
const PRO_SPORTS = ['MLB', 'NBA', 'WNBA'];

// Utility function to map the new player data format to the expected format
// sport parameter is optional - used to exclude NIL for pro sports
const mapNewPlayerFormat = (playerData, sport = null) => {
  // Check if the data is already in the old format
  if (!playerData.personal_info && !playerData.athletic_performance) {
    return playerData; // Already in correct format
  }

  // Determine player type based on position
  let playerType = "hitter";
  if (playerData.personal_info.position.includes("RHP") ||
    playerData.personal_info.position.includes("LHP") ||
    playerData.personal_info.position.includes("P") ||
    playerData.personal_info.position.includes("pitcher")) {
    playerType = "pitcher";
  }
  if (playerData.personal_info.position.includes("C") ||
    playerData.personal_info.position.includes("catcher") ||
    playerData.personal_info.position.toLowerCase() === "catcher") {
    playerType = "catcher";
  }

  // Get primary position
  const position = playerData.personal_info.position.split(',')[0].trim();

  // Extract physical metrics
  const physical = playerData.athletic_performance.physical_metrics || {};

  // Get the right calendar year for stats
  const battingStatsKey = getBattingStatsKey(playerData);
  const pitchingStatsKey = getPitchingStatsKey(playerData);

  // Extract batting stats
  const battingStats = battingStatsKey ?
    playerData.athletic_performance.career_stats.batting[battingStatsKey] || {} : {};

  // Create highlights from key metrics or performance data
  const highlights = [];

  // Add SLAM score if available (prioritize this)
  if (playerData.SLAM_Score) {
    highlights.push({
      metric: "SLAM Score",
      value: playerData.SLAM_Score.toString(),
      rating: playerData.SLAM_Score
    });
  }

  // Add batting metric if available
  if (battingStats.batting_average) {
    highlights.push({
      metric: "Batting Average",
      value: battingStats.batting_average.toFixed(3),
      rating: Math.round(battingStats.batting_average * 100)
    });
  }

  // Add OBP if available
  if (battingStats.on_base_percentage) {
    highlights.push({
      metric: "On-Base %",
      value: battingStats.on_base_percentage.toFixed(3),
      rating: Math.round(battingStats.on_base_percentage * 80) // Adjusted to be less than 100
    });
  }

  // Add stolen bases metric if available
  if (battingStats.stolen_bases) {
    const successRate = battingStats.stolen_bases /
      (battingStats.stolen_bases + (battingStats.caught_stealing || 0)) * 100;

    highlights.push({
      metric: "Stolen Base Success",
      value: `${Math.round(successRate)}%`,
      rating: Math.round(successRate)
    });
  }

  // Add power metrics if available
  if (battingStats.home_runs) {
    highlights.push({
      metric: "Home Runs",
      value: battingStats.home_runs.toString(),
      rating: Math.min(100, battingStats.home_runs * 8) // Scale up to make visual sense
    });
  }

  // Add slugging if available
  if (battingStats.slugging_percentage) {
    highlights.push({
      metric: "Slugging %",
      value: battingStats.slugging_percentage.toFixed(3),
      rating: Math.round(battingStats.slugging_percentage * 70) // Adjusted scale
    });
  }

  // Add pitching metric if pitcher and stats available
  const pitchingStats = playerData.athletic_performance.career_stats.pitching[pitchingStatsKey];
  if (playerType === "pitcher" && pitchingStatsKey) {

    if (physical.fastball_velocity) {
      highlights.push({
        metric: "Fastball Velocity",
        value: `${physical.fastball_velocity} MPH`,
        rating: physical.fastball_percentile || 50
      });
    }

    if (pitchingStats && pitchingStats.era !== undefined && pitchingStats.era !== null) {
      // Lower ERA is better, so we invert the rating (e.g., 3.00 ERA might be 70% rating)
      const eraRating = Math.max(0, 100 - (pitchingStats.era * 10));
      highlights.push({
        metric: "ERA",
        value: pitchingStats.era.toFixed(2),
        rating: eraRating
      });
    }
  }

  // Add catcher metrics if available
  if (playerType === "catcher") {
    // Check if there's any catching-specific stats in progression
    const progression = playerData.athletic_performance.career_stats.batting.progression;
    if (progression) {
      // Try to parse pop time from stats
      const popTimeMatch = JSON.stringify(progression).match(/(\d+\.\d+)(-| )second pop time/i);
      if (popTimeMatch) {
        const popTime = parseFloat(popTimeMatch[1]);
        // 1.9s is elite (95 rating), 2.1s is average (50 rating)
        const popTimeRating = Math.max(0, 100 - ((popTime - 1.8) * 100));
        highlights.push({
          metric: "Pop Time",
          value: `${popTime}s`,
          rating: Math.round(popTimeRating)
        });
      }

      // Parse other catcher metrics if available
      if (progression.caught_stealing_percentage) {
        const csPercent = parseInt(progression.caught_stealing_percentage);
        highlights.push({
          metric: "CS%",
          value: `${csPercent}%`,
          rating: csPercent
        });
      }
    }
  }

  // Extract highlights from key_performance_metrics if available
  if (playerData.athletic_performance.key_performance_metrics &&
    playerData.athletic_performance.key_performance_metrics.length > 0 &&
    highlights.length < 3) {

    // Try to extract meaningful metrics from the key_performance_metrics text
    const metrics = playerData.athletic_performance.key_performance_metrics;

    // Look for exit velocity
    const exitVelocityMatch = JSON.stringify(metrics).match(/Exit velocity: (\d+) mph/i);
    if (exitVelocityMatch && highlights.length < 3) {
      const velocity = parseInt(exitVelocityMatch[1]);
      // Scale: 95+ mph is elite (90+ rating), 85 mph is average (50 rating)
      const velocityRating = Math.max(40, Math.min(95, (velocity - 75) * 2));
      highlights.push({
        metric: "Exit Velocity",
        value: `${velocity} MPH`,
        rating: Math.round(velocityRating)
      });
    }

    // Look for walk rate
    const walkRateMatch = JSON.stringify(metrics).match(/Walk rate: (\d+\.\d+)%/i);
    if (walkRateMatch && highlights.length < 3) {
      const walkRate = parseFloat(walkRateMatch[1]);
      // Scale: 15% is elite (90 rating), 8% is average (50 rating)
      const walkRating = Math.max(20, Math.min(95, 30 + (walkRate * 4)));
      highlights.push({
        metric: "Walk Rate",
        value: `${walkRate}%`,
        rating: Math.round(walkRating)
      });
    }
  }

  // Add NIL potential if available and we need more highlights
  // NIL (Name, Image, Likeness) only applies to college sports, not pro leagues
  const isProSport = sport && PRO_SPORTS.includes(sport);
  let nilValue = playerData.nil_potential_assessment?.projected_annual_nil_value;
  if (!isProSport && highlights.length < 3 && playerData.nil_potential_assessment &&
    playerData.nil_potential_assessment.projected_annual_nil_value) {
    // Handle different formats of NIL value

    // If it's a range like "$5,000-15,000", extract the higher number
    if (typeof nilValue === 'string' && nilValue.includes('-')) {
      const matches = nilValue.match(/\$?([\d,]+)-\$?([\d,]+)/);
      if (matches) {
        nilValue = parseInt(matches[2].replace(/,/g, ''));
      } else {
        nilValue = 5000; // Default if can't parse
      }
    }

    highlights.push({
      metric: "NIL Potential",
      value: typeof nilValue === 'string' ? nilValue : `${nilValue}`,
      rating: Math.min(95, typeof nilValue === 'number' ?
        (nilValue / 200) : 75) // Scale for visual appeal
    });
  }

  // Add national ranking if available
  if (playerData.athletic_performance.national_rankings &&
    playerData.athletic_performance.national_rankings.position &&
    highlights.length < 3) {
    const posRank = playerData.athletic_performance.national_rankings.position;
    highlights.push({
      metric: "Position Rank",
      value: `#${posRank}`,
      rating: Math.max(20, 100 - (posRank * 2)) // Lower rank = higher rating
    });
  }

  // Ensure we have at least 3 highlights
  while (highlights.length < 3) {
    // Add generic highlight if we still need more
    highlights.push({
      metric: "Development Potential",
      value: "High",
      rating: 75
    });
  }

  // Take just the top 3-5 highlights
  if (highlights.length > 5) {
    highlights.splice(5);
  }

  // Set default values for missing physical metrics
  const height = physical.height || "6'0\"";
  const weightVal = physical.weight || 180;
  const weight = weightVal ? `${weightVal} lbs` : "180 lbs";

  // Get batting/throwing hands
  let bats = "", throws = "";
  if (physical.bats_throws) {
    const parts = physical.bats_throws.split('/');
    bats = parts[0];
    throws = parts.length > 1 ? parts[1] : "";
  }

  // Construct the mapped player object
  const mappedPlayer = {
    id: playerData.personal_info.name.toLowerCase().replace(/\s+/g, '-'),
    name: playerData.personal_info.name,
    position: position,
    playerType: playerType,
    slamScore: playerData.SLAM_Score.toString(),
    nilPotential: nilValue,
    era: pitchingStats?.era?.toFixed(2) || 0.00,
    jersey: "", // Not available in new format
    class: playerData.personal_info.year,
    hometown: playerData.personal_info.hometown,
    height: height,
    weight: weight,
    bats: bats,
    throws: throws,
    previousSchool: playerData.personal_info.previous_team || "",
    teamColor: playerData.personal_info.teamColor || "green", // Default
    photo: playerData.personal_info.image || playerData.personal_info.photo,
    stats: {
      batting: {
        avg: battingStats.batting_average || 0,
        ops: battingStats.ops || (
          (battingStats.on_base_percentage || 0) +
          (battingStats.slugging_percentage || 0)
        ),
        hits: battingStats.hits || 0,
        hr: battingStats.home_runs || 0,
        rbi: battingStats.rbis || 0,
        slg: battingStats.slugging_percentage || 0,
        obp: battingStats.on_base_percentage || 0
      }
    },
    highlights: highlights
  };

  // Add pitching stats if available
  if (pitchingStatsKey) {
    const pitchingData = playerData.athletic_performance.career_stats.pitching[pitchingStatsKey] || {};

    mappedPlayer.stats.pitching = {
      era: pitchingData.era || 0,
      wins: pitchingData.wins || 0,
      losses: pitchingData.losses || 0,
      saves: pitchingData.saves || 0,
      ip: pitchingData.innings_pitched || 0,
      so: pitchingData.strikeouts || 0,
      bb: pitchingData.walks || 0,
      whip: pitchingData.whip || 0,
      kper9: pitchingData.strikeouts && pitchingData.innings_pitched ?
        (pitchingData.strikeouts * 9 / pitchingData.innings_pitched) : 0
    };
  }

  // Add catching stats if available
  if (playerType === "catcher") {
    // Try to extract caught stealing percentage from text data
    const csText = JSON.stringify(playerData);
    const csMatch = csText.match(/CS%: (\d+)%|caught stealing percentage: (\d+)|CS: (\d+)%/i);

    mappedPlayer.stats.catching = {
      csPercent: csMatch ? parseInt(csMatch[1] || csMatch[2] || csMatch[3]) : 35 // Default value
    };
  }

  return mappedPlayer;
};

// Helper function to get the correct batting stats key from different formats
const getBattingStatsKey = (playerData) => {
  if (!playerData.athletic_performance || !playerData.athletic_performance.career_stats ||
    !playerData.athletic_performance.career_stats.batting) {
    return null;
  }

  const battingData = playerData.athletic_performance.career_stats.batting;

  // Check for different possible keys
  if (battingData["2025_calendar_year"]) {
    return "2025_calendar_year";
  } else if (battingData["current_calendar_year"]) {
    return "current_calendar_year";
  } else {
    // If no standard key found, return the first key that exists
    const keys = Object.keys(battingData).filter(k =>
      typeof battingData[k] === 'object' &&
      battingData[k] !== null &&
      !k.includes("progression"));

    return keys.length > 0 ? keys[0] : null;
  }
};

// Helper function to get the correct pitching stats key
const getPitchingStatsKey = (playerData) => {
  if (!playerData.athletic_performance || !playerData.athletic_performance.career_stats ||
    !playerData.athletic_performance.career_stats.pitching) {
    return null;
  }

  const pitchingData = playerData.athletic_performance.career_stats.pitching;

  // Check for different possible keys
  if (pitchingData["2025_calendar_year"]) {
    return "2025_calendar_year";
  } else if (pitchingData["current_calendar_year"]) {
    return "current_calendar_year";
  } else {
    // If no standard key found, return the first key that exists
    const keys = Object.keys(pitchingData).filter(k =>
      typeof pitchingData[k] === 'object' &&
      pitchingData[k] !== null &&
      !k.includes("progression"));

    return keys.length > 0 ? keys[0] : null;
  }
};

/**
 * Get current academic season for college sports
 * Academic year runs Aug-July, so season 2024-25 is returned as 2025
 * @returns {number} Current season year
 */
const getCurrentSeason = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const year = now.getFullYear();
  // If before August, use current year as season (e.g., spring 2025 = 2024-25 season = 2025)
  // If August or later, use next year as season (e.g., fall 2025 = 2025-26 season = 2026)
  return month >= 7 ? year + 1 : year;
};

/**
 * Check if a player is on the current roster (not a former player)
 * @param {Object} player - Player object with year/season data
 * @param {number} currentSeason - Current academic season
 * @returns {boolean} True if player is current
 */
const isCurrentPlayer = (player, currentSeason) => {
  // Check various fields that might indicate season/year
  const playerYear = player?.season || player?.roster_year || player?.year;

  // If no year field, check class for graduation status
  if (!playerYear) {
    // Check if class indicates they've graduated
    const playerClass = player?.class?.toLowerCase() || '';
    // Senior/graduate from previous years are former players
    if (playerClass.includes('grad') || playerClass.includes('alumni')) {
      return false;
    }
    // If no year data and not explicitly graduated, assume current
    return true;
  }

  // Convert to number if string (handles "2024", "2024-25", etc.)
  let yearNum;
  if (typeof playerYear === 'string') {
    // Handle formats like "2024-25" -> use first year
    const yearMatch = playerYear.match(/(\d{4})/);
    yearNum = yearMatch ? parseInt(yearMatch[1], 10) : NaN;
  } else {
    yearNum = playerYear;
  }

  // If couldn't parse year, assume current
  if (isNaN(yearNum)) return true;

  // Allow current season and previous season (to handle mid-season updates)
  return yearNum >= currentSeason - 1;
};

// Main MyPlayerProfile component
const MyPlayerProfile = () => {
  const navigate = useNavigate();
  const [teamFilter, setTeamFilter] = useState(true);
  const { playerId } = useParams();
  const [players, setPlayers] = useState(!teamFilter ? samplePlayers?.map(player => mapNewPlayerFormat(player)) : []);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activePosition, setActivePosition] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [activeChatPlayer, setActiveChatPlayer] = useState(null);
  const [uploadTraining, setUploadTraining] = useState(false);
  const [showFormerPlayers, setShowFormerPlayers] = useState(false); // Task 6: Filter stale roster data
  const [nilPreviewPlayer, setNilPreviewPlayer] = useState(null); // NIL Card preview modal
  const [selectedTeam, setSelectedTeam] = useState(null); // Track selected team for banner
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const all_sports = getSetting()?.sports || [];
  const sports_detail = getAllSports()?.filter((v) => all_sports.includes(v.code));
  const [selectedSport, setSelectedSport] = useState(getSelectedSport());

  // Load selected team from session storage
  useEffect(() => {
    const savedTeam = sessionStorage.getItem('selectedTeam');
    if (savedTeam) {
      setSelectedTeam(JSON.parse(savedTeam));
    }
  }, []);

  useEffect(() => {
    if (teamFilter) {
      fetchPlayers();
    }
    else {
      const processedPlayers = samplePlayers?.map(player => mapNewPlayerFormat(player));
      console.log(processedPlayers)
      setFilteredPlayers(processedPlayers);
      setPlayers(samplePlayers)
    }
  }, [teamFilter]);

  // Listen for team changes from Settings and refresh data
  useEffect(() => {
    const handleTeamChange = () => {
      console.log('Team changed event received, refreshing players...');
      // Update selected team for banner
      const savedTeam = sessionStorage.getItem('selectedTeam');
      if (savedTeam) {
        setSelectedTeam(JSON.parse(savedTeam));
      }
      // Update selected sport in case it changed too
      setSelectedSport(getSelectedSport());
      // Refresh players data
      if (teamFilter) {
        fetchPlayers();
      }
    };

    window.addEventListener('teamChange', handleTeamChange);
    return () => window.removeEventListener('teamChange', handleTeamChange);
  }, [teamFilter]);

  const fetchPlayers = async () => {
    const currentSport = getSelectedSport();
    try {
      const savedTeam = sessionStorage.getItem('selectedTeam');
      const team = savedTeam ? JSON.parse(savedTeam) : null;

      if (!team?.team_id) {
        console.warn('No team selected');
        return;
      }

      let mappedPlayers = [];

      // Use MLB roster service for MLB teams (local cache + MLB API fallback)
      if (currentSport === 'MLB') {
        const mlbRoster = await fetchMLBTeamRoster(team.team_id);
        console.log('MLB roster from roster service in MyPlayerProfile:', mlbRoster);
        mappedPlayers = mlbRoster.map(player => mapApiResponseToPlayerFormat({
          player_id: player.mlbId,
          name: player.name,
          position: player.position,
          height: player.height,
          weight: player.weight,
          jersey_number: player.number,
          team: player.team,
          image: player.image_url || player.imageUrl,
          era: player.era,
          batting_average: player.batting_average,
          home_runs: player.home_runs,
          rbi: player.rbi,
        }));
      } else if (currentSport === 'NBA' || currentSport === 'WNBA') {
        // Use basketball data service for NBA/WNBA
        const basketballPlayers = await getBasketballPlayers(currentSport, team?.team_name || team?.name);
        console.log('Basketball roster from service in MyPlayerProfile:', basketballPlayers);
        mappedPlayers = basketballPlayers.map(player => mapApiResponseToPlayerFormat({
          player_id: player.player_id || player.id,
          name: player.player_name || player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
          position: player.position,
          height: player.height,
          weight: player.weight,
          jersey_number: player.jersey_number || player.number,
          team: player.team || team?.team_name,
          image: player.headshot?.href || player.image,
          ppg: player.ppg || player.stats?.ppg,
          apg: player.apg || player.stats?.apg,
          rpg: player.rpg || player.stats?.rpg,
        }));
      } else if (currentSport === 'MBB' || currentSport === 'CBB') {
        // Both MBB and CBB use the official-roasters/search endpoint
        const response = await getMBBRoasters(team?.name);
        mappedPlayers = (response?.data || []).map(mapApiResponseToPlayerFormat);
      } else {
        const response = await apiClient.get(`${currentSport}/players/`, {
          params: {
            team_id: team.team_id,
            skip: 0,
            limit: 100
          }
        });
        mappedPlayers = (response?.data || []).map(mapApiResponseToPlayerFormat);
      }

      setPlayers(mappedPlayers);
      setFilteredPlayers(mappedPlayers);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };
  // Apply all active filters
  const applyFilters = () => {
    let results = [...players];

    // Task 6: Filter out former players unless toggle is on
    if (!showFormerPlayers) {
      const currentSeason = getCurrentSeason();
      results = results.filter(player => isCurrentPlayer(player, currentSeason));
    }

    // Apply player type filter
    if (activeFilter !== 'all') {
      results = results.filter(player => player?.playerType === activeFilter);
    }

    // Apply position filter
    if (activePosition !== 'all') {
      results = results.filter(player => player?.position?.includes(activePosition));
    }

    // Apply search query
    if (searchQuery) {
      results = results.filter(player =>
        player?.Player?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player?.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player?.class?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player?.hometown?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        getPositionName(player?.position)?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPlayers(results);
  };

  // Handle filter changes
  const handleFilter = (filter) => {
    if (filter.type === 'playerType') {
      setActiveFilter(filter.value);
    } else if (filter.type === 'position') {
      setActivePosition(filter.value);
    }
  };

  // Apply filters whenever the active filters change
  useEffect(() => {
    applyFilters();
  }, [activeFilter, activePosition, searchQuery, showFormerPlayers, players]);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  const handleUploadTrainging = () => {
    // setUploadTraining(true);
    navigate(`/integrations?is_stats=${true}`)
  };

  return (
    <div
      className="page-container p-6"
      style={{
        background: 'linear-gradient(180deg, #0a0a0a 0%, #0d0d12 100%)',
      }}
    >
      {/* Ambient Background Glow */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(232,184,0,0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Unified Toolbar */}
      <div
        className="relative rounded-xl p-4 mb-6"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}
      >
        {/* Top Row - Search and Actions */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search players by name, position..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-11 pr-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-yellow-500/30"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#fff',
              }}
            />
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2"
              size={16}
              style={{ color: 'rgba(255,255,255,0.4)' }}
            />
          </div>

          {/* Action Buttons Group */}
          <div className="flex items-center gap-3">
            {/* Sport Selector */}
            <div className="relative">
              <select
                id="sport-select"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="pl-3 pr-8 py-2.5 rounded-lg text-xs font-semibold uppercase tracking-wide appearance-none cursor-pointer transition-all duration-200 focus:outline-none"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.8)',
                }}
              >
                {sports_detail.map((sport) => (
                  <option
                    key={sport.code}
                    value={sport.code}
                    className={'bg-gray-900 text-white'}
                  >
                    {sport.code}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                size={14}
                style={{ color: 'rgba(255,255,255,0.4)' }}
              />
            </div>

            {/* View Mode Toggle */}
            <div
              className="flex items-center rounded-lg p-0.5"
              style={{
                background: 'rgba(255,255,255,0.05)',
              }}
            >
              <button
                onClick={() => setViewMode("grid")}
                className="p-2 rounded-md transition-all duration-200"
                style={{
                  background: viewMode === "grid"
                    ? 'rgba(232,184,0,0.2)'
                    : 'transparent',
                  color: viewMode === "grid"
                    ? '#E8B800'
                    : 'rgba(255,255,255,0.5)',
                }}
              >
                <GridIcon size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className="p-2 rounded-md transition-all duration-200"
                style={{
                  background: viewMode === "list"
                    ? 'rgba(232,184,0,0.2)'
                    : 'transparent',
                  color: viewMode === "list"
                    ? '#E8B800'
                    : 'rgba(255,255,255,0.5)',
                }}
              >
                <List size={16} />
              </button>
            </div>

            {/* Upload Training Stats Button */}
            <button
              onClick={handleUploadTrainging}
              className="px-4 py-2.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105 active:scale-95 flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #E8B800 0%, #FFA500 100%)',
                boxShadow: '0 2px 12px rgba(232,184,0,0.25)',
                color: '#000',
              }}
            >
              <Plus size={14} />
              Import Stats
            </button>
          </div>
        </div>

        {/* Bottom Row - Position Filters */}
        <div
          className="pt-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <PositionFilter
            onFilter={handleFilter}
            activePosition={activePosition}
            setActivePosition={setActivePosition}
          />
        </div>
      </div>

      {/* Team Filter Toggle */}
      <MyPlayerProfileCard
        activePosition={activePosition}
        searchQuery={searchQuery}
        setTeamFilter={setTeamFilter}
        teamFilter={teamFilter}
        viewMode={viewMode}
        setViewMode={setViewMode}
        showFormerPlayers={showFormerPlayers}
        onNilPreview={
          // NIL only applicable to college sports (not MLB, NBA, WNBA)
          !['MLB', 'NBA', 'WNBA'].includes(selectedSport)
            ? (player) => setNilPreviewPlayer(player)
            : undefined
        }
      />

      {/* Players Grid */}
      {viewMode === 'grid' && !teamFilter && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPlayers?.map(player => (
            <PlayerCard
              key={player?.id}
              player={player}
              onClick={() => navigate(`/player-roster/${player?.id}`)}
              onChatClick={(playerId) => setActiveChatPlayer(playerId)}
            />
          ))}

          {filteredPlayers?.length === 0 && (
            <div className={`col-span-full text-center py-10 rounded-lg border ${'bg-gray-800 border-gray-700 text-gray-300'
              }`}>
              <p className={'text-gray-300'}>No players found matching your criteria.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveFilter('all');
                  setActivePosition('all');
                  fetchPlayers();
                }}
                className={`mt-2 ${'text-blue-400 hover:text-blue-300'
                  } text-sm flex items-center mx-auto`}
              >
                <RefreshCw size={14} className="mr-1" />
                Reset filters
              </button>
            </div>
          )}
        </div>
      )}


      {/* Player Chatbot - Only shows when a player is selected */}
      {activeChatPlayer && (
        <PlayerChatbot key={activeChatPlayer} setActiveChatPlayer={setActiveChatPlayer} name={activeChatPlayer} defaultOpened={true} />
      )}
      {uploadTraining && (
        <UploadTrainingStats uploadTraining={uploadTraining} setUploadTraining={setUploadTraining} />
      )}

      {/* NIL Player Card Modal - Mobile Phone Frame Preview */}
      {nilPreviewPlayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative">
            {/* Close button */}
            <button
              onClick={() => setNilPreviewPlayer(null)}
              className="absolute -top-4 -right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Card title */}
            <div className="text-center mb-4">
              <p className="text-sm text-gray-400 uppercase tracking-wider">NIL Athlete Card</p>
              <p className="text-xs text-gray-500">Inspired by On3, Opendorse & NILSU</p>
            </div>

            {/* NIL Player Card in Phone Frame */}
            <NILPlayerCard player={nilPreviewPlayer} showPhoneFrame={true} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPlayerProfile;